import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type CSSProperties } from 'react';

import {
  fmt,
  fmtB,
  fmtDate,
  LVA_COST_CAP,
  LVA_LABEL_PRESETS,
  type CreateLvaCountRound,
  type CreateLvaItem,
  type LvaCondition,
  type LvaCountRoundListRow,
  type LvaGlTieoutRow,
  type LvaGlTieoutStatus,
  type LvaItemListRow,
  type LvaOverdueRound,
  type LvaProactiveItem,
  type LvaStatus,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { Can } from '../components/Can';
import { DepartmentSelect, DimensionFilter } from '../components/dimensions';
import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import { LvaDetailView } from '../components/lva/LvaDetailView';
import {
  LVA_IMPORT_COLS,
  LVA_IMPORT_FIELDS,
  LVA_PREVIEW_COLS,
  LVA_TEMPLATE_EXAMPLES,
  makeLvaParse,
  makeLvaRunImport,
} from '../components/lva/lvaImport';
import { LvaMovementTable } from '../components/lva/LvaMovementTable';
import { LvaQrCode } from '../components/lva/LvaQrCode';
import { SearchSelect } from '../components/SearchSelect';
import { SpreadsheetImportModal } from '../components/SpreadsheetImportModal';
import {
  useActiveCompanyId,
  useBranches,
  useCoa,
  useDownloadLvaLabels,
  useCompanyCapitalizationPolicy,
  useCompanyLvaMovements,
  useCreateCountRound,
  useCreateLvaItem,
  useDepartments,
  useEmployees,
  useLvaCategories,
  useLvaCountExceptions,
  useLvaCountRounds,
  useLvaGlTieout,
  useLvaItems,
  useLvaProactive,
  useUpdateLvaItem,
} from '../lib/queries';
import { exportRows, todayStamp, type ExportColumn } from '../lib/xlsx';

type TabKey = 'register' | 'count' | 'movement' | 'qr' | 'reports';

// Phase-E: sub-tabs inside the Reports card — the original KPI & aging surface,
// and the proactive Alerts & Scheduling worklist.
type ReportSubTab = 'kpi' | 'alerts';
const REPORT_SUBTABS: readonly (readonly [ReportSubTab, string])[] = [
  ['kpi', 'KPI & Aging'],
  ['alerts', 'Alerts & Scheduling'],
];

interface LvaCategory {
  code: string;
  label: string;
  tone: string;
}

interface StatusMeta {
  l: string;
  c: string;
}

interface Totals {
  count: number;
  qty: number;
  value: number;
  inUse: number;
  missing: number;
  pending: number;
}

interface CategoryBreakdown extends LvaCategory {
  count: number;
  qty: number;
  value: number;
}

interface ConceptStep {
  n: string;
  t: string;
  d: string;
  c: string;
}

// 8-category palette per spec §15. categoryName is stored as the Thai label
// in the DB (free text — server doesn't constrain). We match by label here.
const LVA_CATS: readonly LvaCategory[] = [
  { code: 'FURN', label: 'เฟอร์นิเจอร์สำนักงาน', tone: '#7030A0' },
  { code: 'ELEC', label: 'เครื่องใช้ไฟฟ้าเล็ก', tone: '#b54708' },
  { code: 'TOOL', label: 'เครื่องมือช่าง', tone: '#a16207' },
  { code: 'OFFICE', label: 'อุปกรณ์สำนักงาน', tone: '#1a56a6' },
  { code: 'CCTV', label: 'กล้องวงจรปิด / Security', tone: '#117a3d' },
  { code: 'DISPLAY', label: 'ป้าย / อุปกรณ์ Showroom', tone: '#b42318' },
  { code: 'IT_SM', label: 'IT มูลค่าต่ำ', tone: '#0e7490' },
  { code: 'OTHER', label: 'อื่นๆ', tone: '#52525b' },
];

const FALLBACK_CAT: LvaCategory = LVA_CATS[7]!;

function getCatByLabel(label: string): LvaCategory {
  return LVA_CATS.find((x) => x.label === label) ?? FALLBACK_CAT;
}

const LVA_STATUS_META: Readonly<Record<LvaStatus, StatusMeta>> = {
  IN_USE: { l: 'ใช้งาน', c: 'positive' },
  DISPOSED: { l: 'จำหน่าย', c: 'neutral' },
  LOST: { l: 'สูญหาย', c: 'negative' },
};

const LVA_CONDITION_LABEL: Readonly<Record<LvaCondition, string>> = {
  NEW: 'ใหม่',
  GOOD: 'ดี',
  FAIR: 'พอใช้',
  POOR: 'ทรุดโทรม',
  DAMAGED: 'ชำรุด',
};

// Read the cost as number for KPI / table totals — Decimal serializes as string
// from the API per shared/common.ts MoneySchema.
function costNumber(c: LvaItemListRow['cost']): number {
  return typeof c === 'number' ? c : Number(c);
}

// Tie-out (Phase D6): the registerCost / glDebit / variance fields arrive as
// Decimal strings (or numbers) from the API — coerce for display + Excel cells.
function tieoutNum(v: string | number | null | undefined): number {
  if (v === null || v === undefined) return 0;
  return typeof v === 'number' ? v : Number(v);
}

// Tie-out per-row status → badge tone + Thai label. OK is green, DRIFT amber,
// MISSING_JV red (reuses the same .badge tones as the register status pill).
const TIEOUT_STATUS_META: Readonly<Record<LvaGlTieoutStatus, { c: string; l: string }>> = {
  OK: { c: 'positive', l: 'ตรงกัน' },
  DRIFT: { c: 'warning', l: 'ผลต่าง' },
  MISSING_JV: { c: 'negative', l: 'ไม่พบ JV' },
};

const CONCEPT_STEPS: readonly ConceptStep[] = [
  {
    n: '1',
    t: 'ขึ้นทะเบียน LVA',
    d: 'ทุกของ < 50,000 บาท ที่ต้องการ track + QR Tag',
    c: '#1a56a6',
  },
  { n: '2', t: 'Tag & QR Print', d: 'พิมพ์ Sticker ติดของจริง · Scan ดูได้ทันที', c: '#117a3d' },
  { n: '3', t: 'Physical Count', d: 'รอบประจำปี · Mobile App scan QR', c: '#b54708' },
  { n: '4', t: 'Lifecycle', d: 'โอน / จำหน่าย / สูญหาย + Audit Trail', c: '#7030A0' },
];

const SEARCH_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")";

export const Route = createFileRoute('/lva')({
  component: LvaPage,
});

export function LvaPage() {
  const companyId = useActiveCompanyId();
  const [tab, setTab] = useState<TabKey>('register');
  const [q, setQ] = useState<string>('');
  const [catF, setCatF] = useState<string>('all');
  const [statusF, setStatusF] = useState<string>('all');
  const [filterBranchId, setFilterBranchId] = useState<string>('');
  const [filterDepartmentId, setFilterDepartmentId] = useState<string>('');
  const [showAdd, setShowAdd] = useState<boolean>(false);
  const [showQR, setShowQR] = useState<boolean>(false);
  const [showCount, setShowCount] = useState<boolean>(false);
  const [detail, setDetail] = useState<LvaItemListRow | null>(null);
  const [editItem, setEditItem] = useState<LvaItemListRow | null>(null);

  const itemsQuery = useLvaItems(companyId, {
    ...(filterBranchId ? { branchId: filterBranchId } : {}),
    ...(filterDepartmentId ? { departmentId: filterDepartmentId } : {}),
  });
  const items: readonly LvaItemListRow[] = useMemo(() => itemsQuery.data ?? [], [itemsQuery.data]);

  const filtered = useMemo<readonly LvaItemListRow[]>(
    () =>
      items.filter((r) => {
        if (catF !== 'all') {
          const cat = getCatByLabel(r.categoryName);
          if (cat.code !== catF) return false;
        }
        if (statusF !== 'all' && r.status !== statusF) return false;
        if (
          q &&
          !(
            r.code.toLowerCase().includes(q.toLowerCase()) ||
            r.name.toLowerCase().includes(q.toLowerCase())
          )
        )
          return false;
        return true;
      }),
    [items, q, catF, statusF],
  );

  const totals = useMemo<Totals>(
    () => ({
      count: items.length,
      // qty: always 1 per LvaItem (the prototype's qty>1 model is collapsed to
      // 1-item-per-row here — see schema for rationale).
      qty: items.length,
      value: items.reduce((s, i) => s + costNumber(i.cost), 0),
      inUse: items.filter((i) => i.status === 'IN_USE').length,
      missing: items.filter((i) => i.status === 'LOST').length,
      pending: items.filter((i) => i.status === 'IN_USE').length,
    }),
    [items],
  );

  const missingDamaged = items.filter((i) => i.status === 'LOST').length;

  const tabs: readonly (readonly [TabKey, string])[] = [
    ['register', `ทะเบียน LVA (${items.length})`],
    ['count', 'Physical Count'],
    ['movement', 'Movement & Lifecycle'],
    ['qr', 'QR Code Generator'],
    ['reports', 'Reports & Insights'],
  ];

  return (
    <div className="page">
      <PageHead
        crumbs={['บัญชีแยกประเภท', 'Low-Value Asset Register']}
        title="ทะเบียนทรัพย์สินมูลค่าน้อย (LVA Register)"
        actions={
          <>
            <HelpButton guide="lva" />
            <button className="btn" onClick={() => setShowQR(true)}>
              <Icon name="print" size={14} /> พิมพ์ QR Tag
            </button>
            <button className="btn" onClick={() => setShowCount(true)}>
              <Icon name="check" size={14} /> เริ่มตรวจนับ
            </button>
            <Can permission="lva.create">
              <button className="btn primary" onClick={() => setShowAdd(true)}>
                <Icon name="plus" size={14} /> เพิ่ม LVA ใหม่
              </button>
            </Can>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            รายการในทะเบียน
          </div>
          <div className="value tnum">
            {totals.count} <span className="unit">รายการ</span>
          </div>
          <div className="trend faint">
            รวม {fmt(totals.qty)} ชิ้น · {LVA_CATS.length} หมวด
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--brand)' }} />
            มูลค่ารวม (จดไว้)
          </div>
          <div className="value tnum">
            {fmtB(totals.value)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">ลงค่าใช้จ่ายไปแล้ว · Off-balance</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            ใช้งาน (in-use)
          </div>
          <div className="value tnum">
            {totals.inUse} <span className="unit">รายการ</span>
          </div>
          <div className="trend faint">ยังอยู่ในระบบ</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--negative)' }} />
            สูญหาย
          </div>
          <div className="value tnum" style={{ color: 'var(--negative)' }}>
            {missingDamaged} <span className="unit">รายการ</span>
          </div>
          <div className="trend down">ต้องบันทึก & รายงาน</div>
        </div>
      </div>

      <div
        style={{
          background: '#fafafa',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '14px 18px',
          marginBottom: 'var(--section-gap)',
        }}
      >
        <div className="section-title" style={{ margin: '0 0 10px' }}>
          หลักการ — Memorandum Asset Register (Off-balance)
        </div>
        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, fontSize: 14 }}
        >
          {CONCEPT_STEPS.map((s) => (
            <div
              key={s.n}
              style={{
                display: 'flex',
                gap: 10,
                padding: '10px',
                background: 'white',
                borderRadius: 6,
                border: '1px solid var(--border)',
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: s.c,
                  color: 'white',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 700,
                  fontFamily: 'var(--font-display)',
                  flexShrink: 0,
                }}
              >
                {s.n}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{s.t}</div>
                <div className="meta" style={{ marginTop: 2 }}>
                  {s.d}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: 10,
            padding: '10px 14px',
            background: 'white',
            borderRadius: 6,
            border: '1px solid var(--border)',
            fontSize: 14,
          }}
        >
          <b>หมายเหตุ:</b> LVA ลงเป็น{' '}
          <b>ค่าใช้จ่าย (LVA) อัตโนมัติ / เครดิตบัญชีเงินสด-ธนาคารที่เลือก</b> ตอนซื้อแล้ว ·
          ทะเบียนนี้เป็นเพียง <b>Memorandum</b> ไม่กระทบ GL · ใช้ติดตามและตรวจนับเหมือน Fixed Asset
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'register' && (
        <LVARegister
          rows={filtered}
          q={q}
          setQ={setQ}
          catF={catF}
          setCatF={setCatF}
          statusF={statusF}
          setStatusF={setStatusF}
          companyId={companyId}
          filterBranchId={filterBranchId}
          setFilterBranchId={setFilterBranchId}
          filterDepartmentId={filterDepartmentId}
          setFilterDepartmentId={setFilterDepartmentId}
          onDetail={setDetail}
          loading={itemsQuery.isLoading}
        />
      )}
      {tab === 'count' && (
        <LVAPhysicalCount companyId={companyId} onCreateRound={() => setShowCount(true)} />
      )}
      {tab === 'movement' && <LVAMovement />}
      {tab === 'qr' && <LVAQRGen rows={items} companyId={companyId} />}
      {tab === 'reports' && <LVAReports totals={totals} items={items} companyId={companyId} />}

      {showAdd && <LvaFormModal onClose={() => setShowAdd(false)} />}
      {editItem && <LvaFormModal item={editItem} onClose={() => setEditItem(null)} />}
      {showQR && (
        <PrintQRModal count={items.length} companyId={companyId} onClose={() => setShowQR(false)} />
      )}
      {showCount && <CountModal companyId={companyId} onClose={() => setShowCount(false)} />}
      {detail && (
        <LvaDetailModal
          item={detail}
          onClose={() => setDetail(null)}
          onEdit={(it) => {
            setDetail(null);
            setEditItem(it);
          }}
        />
      )}
    </div>
  );
}

interface RegisterProps {
  rows: readonly LvaItemListRow[];
  q: string;
  setQ: (q: string) => void;
  catF: string;
  setCatF: (c: string) => void;
  statusF: string;
  setStatusF: (s: string) => void;
  companyId: string | undefined;
  filterBranchId: string;
  setFilterBranchId: (id: string) => void;
  filterDepartmentId: string;
  setFilterDepartmentId: (id: string) => void;
  onDetail: (i: LvaItemListRow) => void;
  loading: boolean;
}

// ── Register Excel export (Phase D7) ────────────────────────────────────────
// Exports the currently-filtered LvaItem list. cost as a number + acquiredDate
// pre-formatted so the cells read cleanly; qrPrinted as a yes/no flag.
const REGISTER_EXPORT_COLUMNS: ExportColumn<LvaItemListRow>[] = [
  { header: 'รหัส', value: (r) => r.code, width: 18 },
  { header: 'ชื่อ', value: (r) => r.name, width: 28 },
  { header: 'หมวด', value: (r) => r.categoryName, width: 22 },
  { header: 'มูลค่า', value: (r) => costNumber(r.cost), width: 14 },
  { header: 'สาขา', value: (r) => r.branchName ?? '', width: 18 },
  { header: 'สถานที่ใช้งาน', value: (r) => r.location, width: 20 },
  { header: 'ผู้รับผิดชอบ', value: (r) => r.custodianName ?? r.assignedTo, width: 18 },
  { header: 'วันที่ได้มา', value: (r) => fmtDate(r.acquiredDate), width: 14 },
  { header: 'สภาพ', value: (r) => LVA_CONDITION_LABEL[r.condition ?? 'GOOD'], width: 12 },
  { header: 'สถานะ', value: (r) => LVA_STATUS_META[r.status].l, width: 12 },
  { header: 'พิมพ์ QR', value: (r) => (r.qrPrintedAt ? 'ใช่' : 'ไม่'), width: 10 },
  { header: 'Serial', value: (r) => r.serialNumber ?? '', width: 18 },
];

export function LVARegister({
  rows,
  q,
  setQ,
  catF,
  setCatF,
  statusF,
  setStatusF,
  companyId,
  filterBranchId,
  setFilterBranchId,
  filterDepartmentId,
  setFilterDepartmentId,
  onDetail,
  loading,
}: RegisterProps) {
  const [showImport, setShowImport] = useState(false);

  // Masters for the bulk importer: resolve branchCode/departmentCode → id
  // (Phase D8). Categories pass through as a raw uuid (categoryId column).
  const { data: branches = [] } = useBranches(companyId);
  const { data: departments = [] } = useDepartments(companyId);
  const createItem = useCreateLvaItem(companyId);

  const branchByCode = useMemo(
    () => new Map(branches.map((b) => [b.code.toLowerCase(), b.id])),
    [branches],
  );
  const departmentByCode = useMemo(
    () => new Map(departments.map((d) => [d.code.toLowerCase(), d.id])),
    [departments],
  );
  const parse = useMemo(
    () => makeLvaParse({ branchByCode, departmentByCode }),
    [branchByCode, departmentByCode],
  );
  const runImport = useMemo(
    () => makeLvaRunImport((args) => createItem.mutateAsync(args)),
    [createItem],
  );

  const handleExport = () => {
    void exportRows<LvaItemListRow>(
      `ทะเบียน-LVA_${todayStamp()}.xlsx`,
      'ทะเบียน LVA',
      REGISTER_EXPORT_COLUMNS,
      rows,
    );
  };

  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <input
          className="input search w-lg"
          placeholder="ค้นหา รหัส / ชื่อ"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{
            paddingLeft: 32,
            backgroundImage: SEARCH_BG,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '10px center',
          }}
        />
        <select className="select" value={catF} onChange={(e) => setCatF(e.target.value)}>
          <option value="all">หมวด: ทั้งหมด</option>
          {LVA_CATS.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
        <select className="select" value={statusF} onChange={(e) => setStatusF(e.target.value)}>
          <option value="all">สถานะ: ทั้งหมด</option>
          {(Object.entries(LVA_STATUS_META) as [LvaStatus, StatusMeta][]).map(([k, v]) => (
            <option key={k} value={k}>
              {v.l}
            </option>
          ))}
        </select>
        <DimensionFilter
          companyId={companyId}
          branchId={filterBranchId}
          departmentId={filterDepartmentId}
          onBranchChange={setFilterBranchId}
          onDepartmentChange={setFilterDepartmentId}
        />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Can permission="lva.create">
            <button className="btn" onClick={() => setShowImport(true)} disabled={!companyId}>
              <Icon name="upload" size={14} /> นำเข้า Excel
            </button>
          </Can>
          <button className="btn" onClick={handleExport} disabled={rows.length === 0}>
            <Icon name="download" size={14} /> ส่งออก Excel
          </button>
        </div>
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 560 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 50 }}>รูป</th>
              <th>รหัส</th>
              <th>ชื่อ</th>
              <th>หมวด</th>
              <th>สาขา</th>
              <th>สถานที่ใช้งาน</th>
              <th>ผู้รับผิดชอบ</th>
              <th className="num">มูลค่า</th>
              <th>วันที่ซื้อ</th>
              <th>สถานะ</th>
              <th>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={11} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                  กำลังโหลด…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={11} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                  ยังไม่มี LVA ในทะเบียน
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const c = getCatByLabel(r.categoryName);
              const s = LVA_STATUS_META[r.status];
              return (
                <tr key={r.id} onClick={() => onDetail(r)} style={{ cursor: 'pointer' }}>
                  <td>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 6,
                        background: c.tone + '20',
                        color: c.tone,
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {c.code}
                    </div>
                  </td>
                  <td className="code">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <b>{r.code}</b>
                      {r.qrPrintedAt && (
                        <span
                          title="พิมพ์ QR แล้ว"
                          style={{ display: 'inline-flex', color: 'var(--ok, #16a34a)' }}
                        >
                          <Icon name="print" size={12} />
                        </span>
                      )}
                    </span>
                  </td>
                  <td>{r.name}</td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: c.tone }} />
                      {c.label}
                    </span>
                  </td>
                  <td className="muted">{r.branchName ?? '-'}</td>
                  <td className="muted">{r.location}</td>
                  <td className="muted">{r.assignedTo}</td>
                  <td className="num tnum">{fmt(costNumber(r.cost))}</td>
                  <td>{fmtDate(r.acquiredDate)}</td>
                  <td>
                    <span className={`badge ${s.c}`}>
                      <span className="bdot" />
                      {s.l}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn sm ghost"
                      style={{ height: 26, fontSize: 13 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDetail(r);
                      }}
                    >
                      ดู
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length > 0 && (
              <tr className="total">
                <td colSpan={7}>รวม {rows.length} รายการ</td>
                <td className="num tnum">
                  {fmt(rows.reduce((s, r) => s + costNumber(r.cost), 0))}
                </td>
                <td colSpan={3} />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showImport && companyId && (
        <SpreadsheetImportModal<CreateLvaItem>
          title="นำเข้าทะเบียน LVA"
          intro={
            <>
              หนึ่งแถว = หนึ่งรายการสินทรัพย์ — ระบบจะ <b>สร้างใหม่เท่านั้น</b> (ไม่อัปเดตของเดิม)
              และ <b>ลงเป็นค่าใช้จ่าย (LVA) อัตโนมัติ</b> ตอนสร้าง · ระบุ
              <code> branchCode</code>/<code>departmentCode</code> เป็นรหัสสาขา/หน่วยงาน (ถ้ามี
              ต้องมีอยู่จริง)
            </>
          }
          fields={LVA_IMPORT_FIELDS}
          templateColumns={[...LVA_IMPORT_COLS]}
          templateExamples={LVA_TEMPLATE_EXAMPLES}
          templateFilename="lva_register_template.xlsx"
          sheetName="ทะเบียน LVA"
          parse={parse}
          previewColumns={LVA_PREVIEW_COLS}
          runImport={runImport}
          // Each per-row create already invalidates the lva-items cache via
          // useCreateLvaItem's onSuccess, so the register refreshes live as the
          // batch runs — no extra invalidate needed on close.
          onClose={() => setShowImport(false)}
        />
      )}
    </div>
  );
}

// Scope display: the AND of the (optional) branch/dept/category filters, or
// "ทุก Cost Center" when the round covers everything (matches the prototype).
function countScopeLabel(r: LvaCountRoundListRow): string {
  const parts = [r.scopeBranchName, r.scopeDepartmentName, r.scopeCategoryName].filter(
    (x): x is string => !!x,
  );
  return parts.length ? parts.join(' · ') : 'ทุก Cost Center';
}

function LVAPhysicalCount({
  companyId,
  onCreateRound,
}: {
  companyId: string | undefined;
  onCreateRound: () => void;
}) {
  const navigate = useNavigate();
  const roundsQuery = useLvaCountRounds(companyId);
  const rounds = roundsQuery.data ?? [];

  const openRound = (id: string) =>
    void navigate({ to: '/lva/count/$roundId', params: { roundId: id } });

  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>รอบการตรวจนับ LVA</span>
        <div style={{ marginLeft: 'auto' }}>
          <button className="btn sm primary" onClick={onCreateRound}>
            <Icon name="plus" size={12} /> สร้างรอบใหม่
          </button>
        </div>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>รหัสรอบ</th>
            <th>รอบการตรวจ</th>
            <th>วันตรวจตามแผน</th>
            <th>Scope</th>
            <th className="num">รวม</th>
            <th className="num">นับแล้ว</th>
            <th className="num">พบ</th>
            <th className="num">ไม่พบ</th>
            <th className="num">เพิ่ม</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {roundsQuery.isLoading && (
            <tr>
              <td colSpan={11} className="muted" style={{ textAlign: 'center', padding: 18 }}>
                กำลังโหลด…
              </td>
            </tr>
          )}
          {!roundsQuery.isLoading && rounds.length === 0 && (
            <tr>
              <td colSpan={11} className="muted" style={{ textAlign: 'center', padding: 18 }}>
                ยังไม่มีรอบการตรวจนับ
              </td>
            </tr>
          )}
          {rounds.map((r) => {
            // Live per-status tallies live on the round detail (C9); the list row
            // carries the snapshot size (itemCount) — show it as รวม and leave the
            // count columns as — until the round is opened.
            const inProgress = r.status !== 'CLOSED';
            return (
              <tr key={r.id}>
                <td className="code">
                  <b>{r.roundNo}</b>
                </td>
                <td>{r.period}</td>
                <td>{fmtDate(String(r.plannedDate))}</td>
                <td className="muted">{countScopeLabel(r)}</td>
                <td className="num">{r.itemCount}</td>
                <td className="num muted">—</td>
                <td className="num muted">—</td>
                <td className="num muted">—</td>
                <td className="num muted">—</td>
                <td>
                  {inProgress ? (
                    <span className="badge warning">
                      <span className="bdot" />
                      กำลังนับ
                    </span>
                  ) : (
                    <span className="badge positive">
                      <Icon name="check" size={10} /> เสร็จ
                    </span>
                  )}
                </td>
                <td>
                  <button
                    className="btn sm primary"
                    style={{ height: 26, fontSize: 13 }}
                    onClick={() => openRound(r.id)}
                  >
                    {inProgress ? 'นับต่อ →' : 'ดูผล'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div
        style={{
          padding: '14px 18px',
          background: '#fafafa',
          borderTop: '1px solid var(--border)',
          fontSize: 14,
          lineHeight: 1.55,
        }}
      >
        <b>Mobile App:</b> พนักงานใช้แอปสแกน QR Code ตรวจนับได้สะดวก · ระบบ mark ตำแหน่งที่นับ +
        เวลา · เสร็จแล้วระบบสรุป Variance Report อัตโนมัติ
      </div>
    </div>
  );
}

function LVAMovement() {
  const companyId = useActiveCompanyId();
  const movementsQuery = useCompanyLvaMovements(companyId);
  const rows = movementsQuery.data ?? [];
  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>ประวัติการเคลื่อนไหวของ LVA</span>
        <span className="muted" style={{ marginLeft: 'auto', fontSize: 13 }}>
          {movementsQuery.isLoading ? 'กำลังโหลด…' : `${rows.length} รายการ`}
        </span>
      </div>
      <LvaMovementTable rows={rows} />
    </div>
  );
}

function LVAQRGen({
  rows,
  companyId,
}: {
  rows: readonly LvaItemListRow[];
  companyId: string | undefined;
}) {
  // QR payload = the authenticated scan-to-detail deep link. companyId is
  // threaded so a scan resolves the item in the correct company namespace.
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  // Encode the code (matches the server's buildLvaDeepUrl) so the on-screen QR
  // and the printed-PDF QR resolve to the identical URL even if codes ever
  // contain reserved characters.
  const deepUrl = (r: LvaItemListRow) =>
    `${origin}/lva/${encodeURIComponent(r.code)}?c=${encodeURIComponent(companyId ?? '')}`;

  // Mutation wrapper (not raw downloadLvaLabels) so a successful print
  // invalidates lva-items and the printed/ยังไม่พิมพ์ badges flip live.
  const downloadMut = useDownloadLvaLabels(companyId);
  const handlePrint = (layout: '8' | '24') => {
    if (!companyId) return;
    downloadMut.mutate({ filter: 'all', layout, markPrinted: true });
  };

  return (
    <div className="card">
      <div className="card-head">
        <h3>QR Code Generator — พิมพ์ Sticker Tag</h3>
      </div>
      <div className="card-body">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
          {rows.slice(0, 8).map((r) => {
            const printed = !!r.qrPrintedAt;
            return (
              <div
                key={r.id}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  padding: '14px',
                  textAlign: 'center',
                }}
              >
                <div style={{ width: 120, height: 120, margin: '0 auto' }}>
                  <LvaQrCode value={deepUrl(r)} size={120} />
                </div>
                <div style={{ fontWeight: 700, marginTop: 8, fontSize: 13 }}>{r.code}</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>{r.name.slice(0, 28)}</div>
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 11,
                    fontWeight: 700,
                    color: printed ? 'var(--ok, #16a34a)' : 'var(--muted, #888)',
                  }}
                >
                  {printed ? 'พิมพ์แล้ว' : 'ยังไม่พิมพ์'}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 14, display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button className="btn" onClick={() => handlePrint('8')}>
            <Icon name="print" size={14} /> พิมพ์ A4 (8 ดวง/หน้า)
          </button>
          <button className="btn" onClick={() => handlePrint('24')}>
            <Icon name="print" size={14} /> พิมพ์ A4 (24 ดวง/หน้า)
          </button>
          <button className="btn primary" onClick={() => handlePrint('8')}>
            <Icon name="download" size={14} /> Export PDF
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Aging buckets (by acquiredDate, relative to "now") ──────────────────────
interface AgingBucket {
  label: string;
  color: string;
  count: number;
  value: number;
}

const AGING_BUCKET_DEFS: readonly { label: string; color: string; maxYears: number }[] = [
  { label: '≤ 1 ปี', color: '#117a3d', maxYears: 1 },
  { label: '1–2 ปี', color: '#1a56a6', maxYears: 2 },
  { label: '2–3 ปี', color: '#b54708', maxYears: 3 },
  { label: '3–5 ปี', color: '#c2410c', maxYears: 5 },
  { label: '> 5 ปี', color: '#b42318', maxYears: Infinity },
];

const CONDITION_TONE: Readonly<Record<LvaCondition, string>> = {
  NEW: '#117a3d',
  GOOD: '#1a56a6',
  FAIR: '#a16207',
  POOR: '#b54708',
  DAMAGED: '#b42318',
};

const CONDITION_ORDER: readonly LvaCondition[] = ['NEW', 'GOOD', 'FAIR', 'POOR', 'DAMAGED'];

function yearsSince(d: Date | string, now: number): number {
  const t = (d instanceof Date ? d : new Date(d)).getTime();
  if (Number.isNaN(t)) return Infinity;
  return (now - t) / (365.25 * 24 * 60 * 60 * 1000);
}

export function LVAReports({
  totals,
  items,
  companyId,
}: {
  totals: Totals;
  items: readonly LvaItemListRow[];
  companyId: string | undefined;
}) {
  // Phase-E: two sub-tabs inside the Reports card — the existing KPI & aging
  // surface, and an Alerts & Scheduling worklist (driven by useLvaProactive).
  const [reportTab, setReportTab] = useState<ReportSubTab>('kpi');

  // Phase-C count hooks feed the open-exceptions + last-count KPI tiles. The rest
  // is derived client-side from the register (`items`) — no new endpoint.
  const exceptionsQuery = useLvaCountExceptions(companyId);
  const roundsQuery = useLvaCountRounds(companyId);
  const openExceptions = exceptionsQuery.data?.length ?? 0;
  const lastCountDate = useMemo(() => {
    const closed = (roundsQuery.data ?? [])
      .map((r) => r.closedAt)
      .filter((d): d is NonNullable<typeof d> => d != null)
      .map((d) => new Date(d).getTime())
      .filter((t) => !Number.isNaN(t));
    return closed.length ? new Date(Math.max(...closed)) : null;
  }, [roundsQuery.data]);

  const byCat: Record<string, CategoryBreakdown> = {};
  items.forEach((r) => {
    const meta = getCatByLabel(r.categoryName);
    const existing = byCat[meta.code];
    if (!existing) {
      byCat[meta.code] = { ...meta, count: 0, qty: 0, value: 0 };
    }
    const bucket = byCat[meta.code]!;
    bucket.count++;
    bucket.qty += 1;
    bucket.value += costNumber(r.cost);
  });
  const cats = Object.values(byCat).sort((a, b) => b.value - a.value);
  const max = Math.max(...cats.map((c) => c.value), 1);

  // KPI math — loss rate (LOST count & value ÷ total), tagging % (qrPrintedAt set).
  const total = items.length;
  const lostItems = items.filter((i) => i.status === 'LOST');
  const lostValue = lostItems.reduce((s, i) => s + costNumber(i.cost), 0);
  const lossRatePct = total ? (lostItems.length / total) * 100 : 0;
  const taggedCount = items.filter((i) => i.qrPrintedAt != null).length;
  const taggingPct = total ? (taggedCount / total) * 100 : 0;

  // Aging buckets by acquiredDate. Stage Date.now() through useState's lazy
  // initialiser (react-hooks/purity flags Date.now() during render / in useMemo).
  const [nowMs] = useState<number>(() => Date.now());
  const aging = useMemo<AgingBucket[]>(() => {
    const buckets: AgingBucket[] = AGING_BUCKET_DEFS.map((b) => ({
      label: b.label,
      color: b.color,
      count: 0,
      value: 0,
    }));
    items.forEach((r) => {
      const yrs = yearsSince(r.acquiredDate, nowMs);
      const idx = AGING_BUCKET_DEFS.findIndex((b) => yrs <= b.maxYears);
      const bucket = buckets[idx === -1 ? buckets.length - 1 : idx]!;
      bucket.count++;
      bucket.value += costNumber(r.cost);
    });
    return buckets;
  }, [items, nowMs]);
  const agingTotalValue = aging.reduce((s, b) => s + b.value, 0);

  // Condition distribution.
  const conditionCount: Record<LvaCondition, number> = {
    NEW: 0,
    GOOD: 0,
    FAIR: 0,
    POOR: 0,
    DAMAGED: 0,
  };
  items.forEach((r) => {
    const c: LvaCondition = r.condition ?? 'GOOD';
    conditionCount[c] = (conditionCount[c] ?? 0) + 1;
  });
  const conditionMax = Math.max(...CONDITION_ORDER.map((c) => conditionCount[c]), 1);

  return (
    <>
      <div className="page-tabs" style={{ marginBottom: 'var(--section-gap)' }}>
        {REPORT_SUBTABS.map(([k, l]) => (
          <div
            key={k}
            className={`pt ${reportTab === k ? 'active' : ''}`}
            onClick={() => setReportTab(k)}
          >
            {l}
          </div>
        ))}
      </div>

      {reportTab === 'alerts' ? (
        <LVAAlerts companyId={companyId} />
      ) : (
        <>
          <div className="kpi-grid">
            <div className="kpi">
              <div className="label">
                <span className="dot" style={{ background: 'var(--brand)' }} />
                มูลค่าทะเบียน
              </div>
              <div className="value tnum">
                {fmtB(totals.value)} <span className="unit">บาท</span>
              </div>
              <div className="trend faint">{fmt(totals.count)} รายการ · Off-balance</div>
            </div>
            <div className="kpi">
              <div className="label">
                <span className="dot" style={{ background: 'var(--positive)' }} />
                ใช้งาน (in-use)
              </div>
              <div className="value tnum">
                {totals.inUse} <span className="unit">รายการ</span>
              </div>
              <div className="trend faint">ติด QR Tag แล้ว {taggedCount} รายการ</div>
            </div>
            <div className="kpi">
              <div className="label">
                <span className="dot" style={{ background: 'var(--negative)' }} />
                อัตราการสูญหาย
              </div>
              <div className="value tnum" style={{ color: 'var(--negative)' }}>
                {lossRatePct.toFixed(1)}%
              </div>
              <div className="trend down">
                {lostItems.length} รายการ · {fmt(lostValue)} บาท
              </div>
            </div>
            <div className="kpi">
              <div className="label">
                <span className="dot" style={{ background: 'var(--info)' }} />
                ติด QR Tag แล้ว
              </div>
              <div className="value tnum">{taggingPct.toFixed(1)}%</div>
              <div className="trend faint">
                {taggedCount}/{total} รายการ
              </div>
            </div>
          </div>

          <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(2,1fr)' }}>
            <div className="kpi">
              <div className="label">
                <span className="dot" style={{ background: 'var(--warning)' }} />
                รายการค้างตรวจสอบ
              </div>
              <div
                className="value tnum"
                style={{ color: openExceptions ? 'var(--warning)' : undefined }}
              >
                {openExceptions} <span className="unit">รายการ</span>
              </div>
              <div className="trend faint">สูญหาย / พบนอกทะเบียน ที่ยังไม่ปิด</div>
            </div>
            <div className="kpi">
              <div className="label">
                <span className="dot" />
                ตรวจนับล่าสุด
              </div>
              <div className="value tnum" style={{ fontSize: 22 }}>
                {lastCountDate ? fmtDate(lastCountDate) : 'ยังไม่เคยตรวจนับ'}
              </div>
              <div className="trend faint">{(roundsQuery.data ?? []).length} รอบที่ผ่านมา</div>
            </div>
          </div>

          <div className="grid-2">
            <div className="card">
              <div className="card-head">
                <h3>สรุปตามหมวด</h3>
              </div>
              <div className="card-body flush">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>หมวด</th>
                      <th className="num">รายการ</th>
                      <th className="num">ชิ้น</th>
                      <th className="num">มูลค่ารวม</th>
                      <th style={{ width: 140 }}>สัดส่วน</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cats.map((c) => (
                      <tr key={c.code}>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <span
                              style={{ width: 10, height: 10, borderRadius: 2, background: c.tone }}
                            />
                            <b>{c.label}</b>
                          </span>
                        </td>
                        <td className="num">{c.count}</td>
                        <td className="num">{c.qty}</td>
                        <td className="num tnum">
                          <b>{fmt(c.value)}</b>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div
                              style={{
                                flex: 1,
                                height: 6,
                                borderRadius: 3,
                                background: '#f0f0f3',
                                overflow: 'hidden',
                              }}
                            >
                              <div
                                style={{
                                  width: `${(c.value / max) * 100}%`,
                                  height: '100%',
                                  background: c.tone,
                                }}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <h3>การกระจายตามสภาพ</h3>
              </div>
              <div className="card-body">
                <div className="bars" style={{ height: 160 }}>
                  {CONDITION_ORDER.map((c) => (
                    <div className="bcol" key={c}>
                      <div className="bstack">
                        <div
                          className="b1"
                          style={{
                            height: `${(conditionCount[c] / conditionMax) * 100}%`,
                            background: CONDITION_TONE[c],
                          }}
                          title={`${LVA_CONDITION_LABEL[c]} ${conditionCount[c]} รายการ`}
                        />
                      </div>
                      <div className="blabel">{LVA_CONDITION_LABEL[c]}</div>
                      <div className="blabel tnum">
                        <b>{conditionCount[c]}</b>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 'var(--section-gap)' }}>
            <div className="card-head">
              <div>
                <h3>อายุสินทรัพย์ (ตามวันได้มา)</h3>
                <div className="sub">มูลค่ารวม {fmtB(agingTotalValue)} บาท</div>
              </div>
            </div>
            <div className="card-body">
              {aging.map((a) => (
                <div className="aging-row" key={a.label}>
                  <div className="muted">
                    {a.label} <span className="faint">({a.count})</span>
                  </div>
                  <div className="aging-bar">
                    <div
                      className="seg"
                      style={{
                        width: agingTotalValue ? `${(a.value / agingTotalValue) * 100}%` : '0%',
                        background: a.color,
                      }}
                    />
                  </div>
                  <div className="right tnum">
                    <b>{fmt(a.value)}</b> <span className="faint">฿</span>
                  </div>
                </div>
              ))}
              {agingTotalValue === 0 && (
                <div className="muted" style={{ fontSize: 14, marginTop: 8, textAlign: 'center' }}>
                  ยังไม่มีรายการในทะเบียน
                </div>
              )}
            </div>
          </div>

          <LVATieout companyId={companyId} />
        </>
      )}
    </>
  );
}

// ── Alerts & Scheduling (Phase E4) ──────────────────────────────────────────
// A proactive worklist surface driven by useLvaProactive: a KPI strip of the
// headline tallies + a worklist table per category (cycle-count due / warranty
// expiring / overdue count rounds / replacement due / condition-flagged). All
// data is computed server-side (read-only); we only present + empty-state it.
// Reuses .kpi-grid + .badge tones + the standard .tbl/.card chrome.

type AlertTone = 'warning' | 'negative' | 'info' | 'brand' | 'positive';

function AlertKpi({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: number;
  tone: AlertTone;
  hint: string;
}) {
  const toneVar = `var(--${tone === 'brand' ? 'brand' : tone})`;
  return (
    <div className="kpi">
      <div className="label">
        <span className="dot" style={{ background: toneVar }} />
        {label}
      </div>
      <div className="value tnum" style={{ color: value ? toneVar : undefined }}>
        {value} <span className="unit">รายการ</span>
      </div>
      <div className="trend faint">{hint}</div>
    </div>
  );
}

// One worklist card: a category title + a table of code/name/branch + the
// relevant date/days column + a row "ดู" link (item detail or the count tab).
function AlertWorklist<T>({
  title,
  badge,
  rows,
  dateHeader,
  renderDate,
  onRow,
  rowKey,
  cells,
  onExport,
}: {
  title: string;
  badge: AlertTone;
  rows: readonly T[];
  dateHeader: string;
  renderDate: (r: T) => React.ReactNode;
  onRow: (r: T) => void;
  rowKey: (r: T) => string;
  cells: (r: T) => { code: string; name: string; branch: string };
  onExport: () => void;
}) {
  return (
    <div className="card" style={{ marginTop: 'var(--section-gap)' }}>
      <div className="card-head">
        <h3>
          {title} <span className={`badge ${badge}`}>{rows.length}</span>
        </h3>
        <button
          className="btn sm ghost"
          onClick={onExport}
          disabled={rows.length === 0}
          title="ส่งออกรายการนี้เป็น Excel"
        >
          <Icon name="download" size={14} /> ส่งออก Excel
        </button>
      </div>
      <div className="card-body flush">
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 160 }}>รหัส</th>
              <th>ชื่อ</th>
              <th>สาขา</th>
              <th>{dateHeader}</th>
              <th style={{ width: 70 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const c = cells(r);
              return (
                <tr key={rowKey(r)}>
                  <td>
                    <b>{c.code}</b>
                  </td>
                  <td>{c.name}</td>
                  <td className="muted">{c.branch || '—'}</td>
                  <td>{renderDate(r)}</td>
                  <td>
                    <button
                      className="btn sm ghost"
                      style={{ height: 26, fontSize: 13 }}
                      onClick={() => onRow(r)}
                    >
                      ดู
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่มีรายการ
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Replacement forecast (Phase E5) ─────────────────────────────────────────
// Buckets the proactive items by years-to-replacement (replaceByDate vs now):
// already overdue / within 1y / 1–3y / >3y. Reuses the .aging-row/.aging-bar
// idiom from the KPI tab (bar width is each bucket's share of the item count).
interface ForecastBucket {
  label: string;
  color: string;
  // upper bound in years (inclusive); Infinity = the catch-all >3y bucket. A
  // replaceByDate in the past lands in the dedicated "overdue" bucket below.
  maxYears: number;
}

const FORECAST_BUCKET_DEFS: readonly ForecastBucket[] = [
  { label: 'เกินกำหนด', color: '#b42318', maxYears: 0 },
  { label: 'ภายใน 1 ปี', color: '#c2410c', maxYears: 1 },
  { label: '1–3 ปี', color: '#b54708', maxYears: 3 },
  { label: 'เกิน 3 ปี', color: '#117a3d', maxYears: Infinity },
];

// years-from-now to a future replaceByDate (negative once past → the overdue
// bucket). Matches yearsSince()'s 365.25-day year so the two views agree.
function yearsUntil(d: Date | string, now: number): number {
  const t = (d instanceof Date ? d : new Date(d)).getTime();
  if (Number.isNaN(t)) return Infinity;
  return (t - now) / (365.25 * 24 * 60 * 60 * 1000);
}

function bucketForecast(
  items: readonly LvaProactiveItem[],
  now: number,
): { label: string; color: string; count: number }[] {
  const counts = FORECAST_BUCKET_DEFS.map((b) => ({
    label: b.label,
    color: b.color,
    count: 0,
  }));
  items.forEach((it) => {
    const yrs = yearsUntil(it.replaceByDate, now);
    // ≤ 0 years → already due (overdue bucket, index 0); else first def whose
    // upper bound covers it.
    // +0.01y (~3.6d) slack absorbs the drift between the server's addUtcYears
    // (exact calendar years) and yearsUntil's 365.25-day divisor — without it an
    // item exactly N calendar years out reads as N.00…y and skips into the next
    // bucket (e.g. exactly 3y → ">3y" instead of "1–3 ปี"). The slack is far below
    // any 1-year bucket span, so interior items are unaffected.
    const idx = yrs <= 0 ? 0 : FORECAST_BUCKET_DEFS.findIndex((b) => yrs <= b.maxYears + 0.01);
    counts[idx === -1 ? FORECAST_BUCKET_DEFS.length - 1 : idx]!.count++;
  });
  return counts;
}

// Worklist Excel export column specs (Phase E5). Each worklist's "ส่งออก Excel"
// button feeds its rows through exportRows with the matching spec.
const FORECAST_TIER_LABEL: Readonly<Record<LvaProactiveItem['riskTier'], string>> = {
  HIGH: 'สูง',
  MEDIUM: 'กลาง',
  LOW: 'ต่ำ',
};

const ITEM_EXPORT_BASE: ExportColumn<LvaProactiveItem>[] = [
  { header: 'รหัส', value: (r) => r.code, width: 18 },
  { header: 'ชื่อ', value: (r) => r.name, width: 28 },
  { header: 'สาขา', value: (r) => r.branchName ?? '', width: 18 },
  { header: 'หมวด', value: (r) => r.categoryName, width: 20 },
  { header: 'ระดับความเสี่ยง', value: (r) => FORECAST_TIER_LABEL[r.riskTier], width: 14 },
];

const CYCLE_EXPORT_COLUMNS: ExportColumn<LvaProactiveItem>[] = [
  ...ITEM_EXPORT_BASE,
  {
    header: 'นับล่าสุด',
    value: (r) => (r.lastCountedAt ? fmtDate(r.lastCountedAt) : ''),
    width: 14,
  },
  { header: 'วันค้างนับ', value: (r) => r.daysSinceCount ?? '', width: 12 },
];

const WARRANTY_EXPORT_COLUMNS: ExportColumn<LvaProactiveItem>[] = [
  ...ITEM_EXPORT_BASE,
  {
    header: 'วันหมดประกัน',
    value: (r) => (r.warrantyExpiry ? fmtDate(r.warrantyExpiry) : ''),
    width: 14,
  },
  { header: 'เหลือ (วัน)', value: (r) => r.daysToWarranty ?? '', width: 12 },
];

const REPLACE_EXPORT_COLUMNS: ExportColumn<LvaProactiveItem>[] = [
  ...ITEM_EXPORT_BASE.slice(0, 4),
  { header: 'อายุการใช้งาน (ปี)', value: (r) => r.usefulLifeYears, width: 16 },
  { header: 'ครบกำหนดเปลี่ยน', value: (r) => fmtDate(r.replaceByDate), width: 16 },
];

const CONDITION_EXPORT_COLUMNS: ExportColumn<LvaProactiveItem>[] = [
  ...ITEM_EXPORT_BASE.slice(0, 4),
  { header: 'สภาพ', value: (r) => LVA_CONDITION_LABEL[r.condition], width: 14 },
];

const OVERDUE_ROUND_EXPORT_COLUMNS: ExportColumn<LvaOverdueRound>[] = [
  { header: 'รอบ', value: (r) => r.roundNo, width: 20 },
  { header: 'งวด', value: (r) => r.period, width: 10 },
  { header: 'สถานะ', value: (r) => r.status, width: 14 },
  { header: 'กำหนดส่ง', value: (r) => fmtDate(r.dueDate), width: 14 },
  { header: 'เกินกำหนด (วัน)', value: (r) => r.daysOverdue, width: 14 },
];

// Build a worklist export handler: filename = <title>_LVA_<todayStamp>.xlsx.
function exportWorklist<T>(
  title: string,
  columns: ExportColumn<T>[],
  rows: readonly T[],
): () => void {
  return () => void exportRows<T>(`${title}_LVA_${todayStamp()}.xlsx`, title, columns, rows);
}

export function LVAAlerts({ companyId }: { companyId: string | undefined }) {
  const navigate = useNavigate();
  const proactiveQuery = useLvaProactive(companyId);
  const summary = proactiveQuery.data;

  const items = useMemo<readonly LvaProactiveItem[]>(() => summary?.items ?? [], [summary]);
  const overdueRounds: readonly LvaOverdueRound[] = summary?.overdueRounds ?? [];
  const counts = summary?.counts ?? {
    cycleDue: 0,
    warrantyDue: 0,
    replaceDue: 0,
    conditionFlag: 0,
    overdueRounds: 0,
  };

  const cycleDue = items.filter((i) => i.cycleDue);
  const warrantyDue = items.filter((i) => i.warrantyDue);
  const replaceDue = items.filter((i) => i.replaceDue);
  const conditionFlagged = items.filter((i) => i.conditionFlag);

  // Replacement forecast — bucket ALL items by years-to-replacement. Stage
  // Date.now() through useState's lazy initialiser (react-hooks/purity bars
  // Date.now() during render / inside useMemo).
  const [nowMs] = useState<number>(() => Date.now());
  const forecast = useMemo(() => bucketForecast(items, nowMs), [items, nowMs]);
  const forecastMax = Math.max(...forecast.map((b) => b.count), 1);
  const forecastTotal = forecast.reduce((s, b) => s + b.count, 0);

  const itemCells = (r: LvaProactiveItem) => ({
    code: r.code,
    name: r.name,
    branch: r.branchName ?? '',
  });
  const gotoItem = (r: LvaProactiveItem) =>
    void navigate({ to: '/lva/$code', params: { code: r.code } });

  return (
    <>
      <div className="kpi-grid">
        <AlertKpi
          label="ถึงกำหนดนับ"
          value={counts.cycleDue}
          tone="warning"
          hint="เกินรอบตรวจนับตามระดับความเสี่ยง"
        />
        <AlertKpi
          label="ประกันใกล้หมด"
          value={counts.warrantyDue}
          tone="info"
          hint="ประกันหมด/ใกล้หมดภายใน 90 วัน"
        />
        <AlertKpi
          label="รอบเกินกำหนด"
          value={counts.overdueRounds}
          tone="negative"
          hint="รอบตรวจนับที่เลยกำหนดและยังไม่ปิด"
        />
        <AlertKpi
          label="ถึงเวลาเปลี่ยน"
          value={counts.replaceDue}
          tone="brand"
          hint="พ้นอายุการใช้งานตามระดับความเสี่ยง"
        />
        <AlertKpi
          label="สภาพแย่"
          value={counts.conditionFlag}
          tone="negative"
          hint="สภาพ ทรุดโทรม / ชำรุด"
        />
      </div>

      <div className="card" style={{ marginTop: 'var(--section-gap)' }}>
        <div className="card-head">
          <div>
            <h3>พยากรณ์การเปลี่ยนทดแทน</h3>
            <div className="sub">
              จัดกลุ่มตามจำนวนปีที่เหลือก่อนครบอายุการใช้งาน (ได้มา + อายุตามระดับความเสี่ยง)
            </div>
          </div>
        </div>
        <div className="card-body">
          {forecast.map((b) => (
            <div className="aging-row" key={b.label}>
              <div className="muted">
                {b.label} <span className="faint">({b.count})</span>
              </div>
              <div className="aging-bar">
                <div
                  className="seg"
                  style={{
                    width: `${(b.count / forecastMax) * 100}%`,
                    background: b.color,
                  }}
                />
              </div>
              <div className="right tnum">
                <b>{b.count}</b> <span className="faint">รายการ</span>
              </div>
            </div>
          ))}
          {forecastTotal === 0 && (
            <div className="muted" style={{ fontSize: 14, marginTop: 8, textAlign: 'center' }}>
              ยังไม่มีรายการในทะเบียน
            </div>
          )}
        </div>
      </div>

      <AlertWorklist<LvaProactiveItem>
        title="ถึงกำหนดนับ"
        badge="warning"
        rows={cycleDue}
        onExport={exportWorklist('ถึงกำหนดนับ', CYCLE_EXPORT_COLUMNS, cycleDue)}
        dateHeader="นับล่าสุด / วันที่ค้าง"
        renderDate={(r) => (
          <span>
            {r.lastCountedAt ? fmtDate(r.lastCountedAt) : 'ยังไม่เคยนับ'}
            {r.daysSinceCount != null && <span className="faint"> · {r.daysSinceCount} วัน</span>}
          </span>
        )}
        onRow={gotoItem}
        rowKey={(r) => r.id}
        cells={itemCells}
      />

      <AlertWorklist<LvaProactiveItem>
        title="ประกันใกล้หมด"
        badge="info"
        rows={warrantyDue}
        onExport={exportWorklist('ประกันใกล้หมด', WARRANTY_EXPORT_COLUMNS, warrantyDue)}
        dateHeader="วันหมดประกัน"
        renderDate={(r) => (
          <span>
            {r.warrantyExpiry ? fmtDate(r.warrantyExpiry) : '—'}
            {r.daysToWarranty != null && (
              <span className="faint">
                {' '}
                ·{' '}
                {r.daysToWarranty < 0
                  ? `เลย ${-r.daysToWarranty} วัน`
                  : `อีก ${r.daysToWarranty} วัน`}
              </span>
            )}
          </span>
        )}
        onRow={gotoItem}
        rowKey={(r) => r.id}
        cells={itemCells}
      />

      <AlertWorklist<LvaProactiveItem>
        title="ถึงเวลาเปลี่ยน"
        badge="brand"
        rows={replaceDue}
        onExport={exportWorklist('ถึงเวลาเปลี่ยน', REPLACE_EXPORT_COLUMNS, replaceDue)}
        dateHeader="ครบอายุการใช้งาน"
        renderDate={(r) => (
          <span>
            {fmtDate(r.replaceByDate)}
            <span className="faint"> · อายุ {r.usefulLifeYears} ปี</span>
          </span>
        )}
        onRow={gotoItem}
        rowKey={(r) => r.id}
        cells={itemCells}
      />

      <AlertWorklist<LvaProactiveItem>
        title="สภาพแย่"
        badge="negative"
        rows={conditionFlagged}
        onExport={exportWorklist('สภาพแย่', CONDITION_EXPORT_COLUMNS, conditionFlagged)}
        dateHeader="สภาพ"
        renderDate={(r) => <span className="muted">{LVA_CONDITION_LABEL[r.condition]}</span>}
        onRow={gotoItem}
        rowKey={(r) => r.id}
        cells={itemCells}
      />

      <AlertWorklist<LvaOverdueRound>
        title="รอบเกินกำหนด"
        badge="negative"
        rows={overdueRounds}
        onExport={exportWorklist('รอบเกินกำหนด', OVERDUE_ROUND_EXPORT_COLUMNS, overdueRounds)}
        dateHeader="กำหนด / เกินกำหนด"
        renderDate={(r) => (
          <span>
            {fmtDate(r.dueDate)}
            <span className="faint"> · เลย {r.daysOverdue} วัน</span>
          </span>
        )}
        onRow={() => void navigate({ to: '/lva' })}
        rowKey={(r) => r.id}
        cells={(r) => ({ code: r.roundNo, name: `งวด ${r.period}`, branch: r.status })}
      />
    </>
  );
}

// ── Memo-vs-GL tie-out (Phase D6) ───────────────────────────────────────────
// Proves every off-balance LVA register row has a matching GL expense posting.
// Server (useLvaGlTieout) reconciles each item's registerCost against the linked
// JV's LVA-expense debit and flags OK / DRIFT / MISSING_JV; this view shows the
// detail table + a summary footer + an Excel export over the returned rows.
const TIEOUT_EXPORT_COLUMNS: ExportColumn<LvaGlTieoutRow>[] = [
  { header: 'งวด', value: (r) => r.period, width: 10 },
  { header: 'รหัส', value: (r) => r.code, width: 18 },
  { header: 'ชื่อ', value: (r) => r.name, width: 28 },
  { header: 'สาขา', value: (r) => r.branchName ?? '', width: 18 },
  { header: 'วันที่ได้มา', value: (r) => fmtDate(r.acquiredDate), width: 14 },
  { header: 'มูลค่าทะเบียน', value: (r) => tieoutNum(r.registerCost), width: 16 },
  { header: 'เดบิต GL', value: (r) => tieoutNum(r.glDebit), width: 16 },
  { header: 'ผลต่าง', value: (r) => tieoutNum(r.variance), width: 14 },
  { header: 'สถานะ', value: (r) => r.status, width: 14 },
];

export function LVATieout({ companyId }: { companyId: string | undefined }) {
  const [period, setPeriod] = useState<string>('');
  const [branchId, setBranchId] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<string>('');

  const tieoutQuery = useLvaGlTieout(companyId, {
    ...(period ? { period } : {}),
    ...(branchId ? { branchId } : {}),
    ...(departmentId ? { departmentId } : {}),
  });
  const rows: readonly LvaGlTieoutRow[] = tieoutQuery.data?.rows ?? [];
  const summary = tieoutQuery.data?.summary;
  const loading = tieoutQuery.isLoading;

  const handleExport = () => {
    void exportRows<LvaGlTieoutRow>(
      `กระทบยอด-GL_LVA_${todayStamp()}.xlsx`,
      'กระทบยอด GL',
      TIEOUT_EXPORT_COLUMNS,
      rows,
    );
  };

  return (
    <div className="card" style={{ marginTop: 'var(--section-gap)' }}>
      <div className="card-head">
        <div>
          <h3>กระทบยอด GL (Memorandum vs GL)</h3>
          <div className="sub">
            ทุกรายการในทะเบียนต้องมีบันทึกค่าใช้จ่าย (JV) ที่ตรงกัน · ตรวจสอบ OK · ผลต่าง · ไม่พบ JV
          </div>
        </div>
        <button className="btn" onClick={handleExport} disabled={rows.length === 0}>
          <Icon name="download" size={14} /> ส่งออก Excel
        </button>
      </div>
      <div className="card-body">
        <div className="filter-row" style={{ marginBottom: 12 }}>
          <input
            className="input"
            placeholder="งวด (YYYYMM)"
            value={period}
            maxLength={6}
            onChange={(e) => setPeriod(e.target.value.replace(/[^0-9]/g, ''))}
            style={{ width: 150 }}
          />
          <DimensionFilter
            companyId={companyId}
            branchId={branchId}
            departmentId={departmentId}
            onBranchChange={setBranchId}
            onDepartmentChange={setDepartmentId}
          />
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 480 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>รหัส</th>
                <th>ชื่อ</th>
                <th>สาขา</th>
                <th>วันที่ได้มา</th>
                <th className="num">มูลค่าทะเบียน</th>
                <th className="num">เดบิต GL</th>
                <th className="num">ผลต่าง</th>
                <th>สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                    กำลังโหลด…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                    ไม่มีรายการในงวด/ขอบเขตที่เลือก
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const meta = TIEOUT_STATUS_META[r.status];
                const variance = tieoutNum(r.variance);
                return (
                  <tr key={r.lvaItemId}>
                    <td className="code">
                      <b>{r.code}</b>
                    </td>
                    <td>{r.name}</td>
                    <td className="muted">{r.branchName ?? '-'}</td>
                    <td>{fmtDate(r.acquiredDate)}</td>
                    <td className="num tnum">{fmt(tieoutNum(r.registerCost))}</td>
                    <td className="num tnum">{fmt(tieoutNum(r.glDebit))}</td>
                    <td
                      className="num tnum"
                      style={variance !== 0 ? { color: 'var(--negative)' } : undefined}
                    >
                      {fmt(variance)}
                    </td>
                    <td>
                      <span className={`badge ${meta.c}`} title={meta.l}>
                        <span className="bdot" />
                        {r.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {summary && rows.length > 0 && (
                <tr className="total">
                  <td colSpan={4}>
                    รวม {summary.rowCount} รายการ · OK {summary.okCount} · DRIFT{' '}
                    {summary.driftCount} · MISSING_JV {summary.missingJvCount}
                  </td>
                  <td className="num tnum">{fmt(tieoutNum(summary.totalRegisterCost))}</td>
                  <td className="num tnum">{fmt(tieoutNum(summary.totalGlDebit))}</td>
                  <td
                    className="num tnum"
                    style={
                      tieoutNum(summary.totalVariance) !== 0
                        ? { color: 'var(--negative)' }
                        : undefined
                    }
                  >
                    {fmt(tieoutNum(summary.totalVariance))}
                  </td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function LvaFormModal({ item, onClose }: { item?: LvaItemListRow; onClose: () => void }) {
  const companyId = useActiveCompanyId();
  const branchesQuery = useBranches(companyId);
  const coaQuery = useCoa(companyId);
  const categoriesQuery = useLvaCategories(companyId);
  const employeesQuery = useEmployees(companyId);
  const policyQuery = useCompanyCapitalizationPolicy(companyId);
  const createMut = useCreateLvaItem(companyId);
  const updateMut = useUpdateLvaItem(companyId);
  const isEdit = !!item;

  const today = new Date().toISOString().slice(0, 10);
  const [name, setName] = useState<string>(item?.name ?? '');
  const [categoryId, setCategoryId] = useState<string>(item?.categoryId ?? '');
  const [cost, setCost] = useState<string>(item ? String(costNumber(item.cost)) : '');
  const [acquiredDate, setAcquiredDate] = useState<string>(
    item ? String(item.acquiredDate).slice(0, 10) : today,
  );
  const [branchId, setBranchId] = useState<string>(item?.branchId ?? '');
  const [departmentId, setDepartmentId] = useState<string>(item?.departmentId ?? '');
  const [custodianEmployeeId, setCustodianEmployeeId] = useState<string>(
    item?.custodianEmployeeId ?? '',
  );
  // Free-text custodian is only a fallback on create when no employee is picked;
  // the unified form drives the custodian via the employee SearchSelect, so this
  // value is read-only here (prefilled from the item in edit mode).
  const [assignedTo] = useState<string>(item?.assignedTo ?? '');
  const [location, setLocation] = useState<string>(item?.location ?? '');
  const [serialNumber, setSerialNumber] = useState<string>(item?.serialNumber ?? '');
  const [warrantyExpiry, setWarrantyExpiry] = useState<string>(
    item?.warrantyExpiry ? String(item.warrantyExpiry).slice(0, 10) : '',
  );
  const [condition, setCondition] = useState<LvaCondition>(item?.condition ?? 'GOOD');
  const [sourceDocRef, setSourceDocRef] = useState<string>(item?.sourceDocRef ?? '');
  const [quantity, setQuantity] = useState<string>(item ? String(item.quantity ?? 1) : '1');
  const [unit, setUnit] = useState<string>(item?.unit ?? '');
  const [cashAccountCode, setCashAccountCode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const branches = branchesQuery.data ?? [];
  const categories = useMemo(() => categoriesQuery.data ?? [], [categoriesQuery.data]);
  const employees = useMemo(() => employeesQuery.data ?? [], [employeesQuery.data]);

  // Advisory client cap from the per-company capitalization/materiality policy
  // (fallback LVA_COST_CAP). The authoritative check is server-side.
  const cap = useMemo(() => {
    const t = policyQuery.data?.threshold;
    const n = t == null ? NaN : Number(t);
    return Number.isFinite(n) && n > 0 ? n : LVA_COST_CAP;
  }, [policyQuery.data]);

  const cashAccounts = useMemo(
    () =>
      (coaQuery.data ?? []).filter(
        (c) =>
          c.isActive &&
          !c.isControl &&
          c.type === 'ASSET' &&
          c.subGroup === 'เงินสดและเงินฝากธนาคาร',
      ),
    [coaQuery.data],
  );
  const effectiveCashCode = cashAccountCode || (cashAccounts[0]?.code ?? '');
  const selectedCashAccount = cashAccounts.find((a) => a.code === effectiveCashCode);

  const labelStyle: CSSProperties = {
    fontSize: 14,
    color: 'var(--text-muted)',
    fontWeight: 500,
    display: 'block',
    marginBottom: 6,
  };

  const onSubmit = () => {
    setError(null);
    const costN = Number(cost);
    const qtyN = Math.trunc(Number(quantity));
    if (!name.trim()) return setError('กรุณากรอกชื่อสินค้า');
    if (!Number.isFinite(costN) || costN <= 0) return setError('กรุณากรอกมูลค่า > 0');
    if (costN >= cap)
      return setError(
        `มูลค่าต้อง < ${fmt(cap)} (เกินนโยบายการตั้งเป็นสินทรัพย์/ความมีนัยสำคัญของบริษัท ` +
          `(TFRS for NPAEs) — ต้องลงทะเบียนเป็นทรัพย์สินถาวรแทน)`,
      );
    if (!Number.isFinite(qtyN) || qtyN < 1) return setError('จำนวนต้อง >= 1');
    if (!location.trim()) return setError('กรุณาระบุสถานที่ใช้งาน');

    // M6: the server requires a non-empty assignedTo (CreateLvaItemSchema). The
    // form derives assignedTo from the picked custodian's name, with the legacy
    // free-text value as a fallback — so on CREATE require at least one of them,
    // otherwise the submit would 400 server-side. (Edit doesn't touch custodian.)
    const custodianNameForCheck = employees.find((e) => e.id === custodianEmployeeId)?.nameTh;
    if (!isEdit && !(custodianNameForCheck ?? assignedTo).trim()) {
      return setError('กรุณาเลือกผู้รับผิดชอบ (ผู้ถือครอง)');
    }

    if (isEdit) {
      updateMut.mutate(
        {
          id: item.id,
          data: {
            name,
            categoryId: categoryId || undefined,
            serialNumber: serialNumber || null,
            warrantyExpiry: warrantyExpiry || null,
            condition,
            sourceDocRef: sourceDocRef || null,
            quantity: qtyN,
            unit: unit || null,
            location,
          },
        },
        {
          onSuccess: () => onClose(),
          onError: (e: unknown) => setError(e instanceof Error ? e.message : 'บันทึกล้มเหลว'),
        },
      );
      return;
    }

    const custodianName = employees.find((e) => e.id === custodianEmployeeId)?.nameTh;
    createMut.mutate(
      {
        data: {
          name,
          categoryName: categories.find((c) => c.id === categoryId)?.nameTh ?? FALLBACK_CAT.label,
          categoryId: categoryId || undefined,
          custodianEmployeeId: custodianEmployeeId || undefined,
          branchId: branchId || null,
          departmentId: departmentId || null,
          acquiredDate,
          cost: costN,
          location,
          assignedTo: custodianName ?? assignedTo,
          serialNumber: serialNumber || undefined,
          warrantyExpiry: warrantyExpiry || undefined,
          condition,
          sourceDocRef: sourceDocRef || undefined,
          quantity: qtyN,
          unit: unit || undefined,
          cashAccountCode: effectiveCashCode,
        },
      },
      {
        onSuccess: () => onClose(),
        onError: (e: unknown) => setError(e instanceof Error ? e.message : 'บันทึกล้มเหลว'),
      },
    );
  };

  const busy = createMut.isPending || updateMut.isPending;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 760,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              {isEdit ? `แก้ไข ${item.code}` : 'เพิ่ม LVA ใหม่'}
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              {isEdit ? (
                <>แก้ไขข้อมูลทะเบียน · มูลค่า / วันที่ซื้อ แก้ไม่ได้ (ลง JV ไปแล้ว)</>
              ) : (
                <>
                  เลขที่ใหม่ <b>LVA-YYYY-NNNNN</b> (Auto) · cost ต้อง &lt; {fmt(cap)}
                </>
              )}
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div
          style={{
            padding: '22px',
            overflow: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>ชื่อสินค้า</label>
              <input
                className="input"
                placeholder="เช่น เก้าอี้ Ergonomic"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={labelStyle} id="lva-cat-label">
                หมวด
              </label>
              <SearchSelect
                value={categoryId}
                onChange={setCategoryId}
                placeholder="— เลือกหมวด —"
                options={categories.map((c) => ({ value: c.id, label: c.nameTh }))}
                getLabel={(v) => categories.find((c) => c.id === v)?.nameTh ?? ''}
                ariaLabel="หมวด"
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>มูลค่า (&lt; {fmt(cap)})</label>
              <input
                className="input tnum"
                type="number"
                placeholder="3,500"
                value={cost}
                disabled={isEdit}
                onChange={(e) => setCost(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
            <div>
              <label style={labelStyle}>บัญชีเครดิต (เงินสด-ธนาคาร)</label>
              <select
                className="select"
                value={effectiveCashCode}
                disabled={isEdit}
                onChange={(e) => setCashAccountCode(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              >
                {cashAccounts.length === 0 && <option value="">— กำลังโหลด —</option>}
                {cashAccounts.map((a) => (
                  <option key={a.code} value={a.code}>
                    {a.code} · {a.nameTh}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>วันที่ซื้อ</label>
              <input
                className="input"
                type="date"
                value={acquiredDate}
                disabled={isEdit}
                onChange={(e) => setAcquiredDate(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Serial / รหัสเครื่อง</label>
              <input
                className="input"
                placeholder="เช่น SN-12345"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={labelStyle}>หมดประกัน</label>
              <input
                className="input"
                type="date"
                value={warrantyExpiry}
                onChange={(e) => setWarrantyExpiry(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={labelStyle}>สภาพ</label>
              <select
                className="select"
                value={condition}
                onChange={(e) => setCondition(e.target.value as LvaCondition)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              >
                {(['NEW', 'GOOD', 'FAIR', 'POOR', 'DAMAGED'] as const).map((cd) => (
                  <option key={cd} value={cd}>
                    {LVA_CONDITION_LABEL[cd]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>เลขที่เอกสารอ้างอิง</label>
              <input
                className="input"
                placeholder="เช่น INV-2026-001"
                value={sourceDocRef}
                onChange={(e) => setSourceDocRef(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={labelStyle}>จำนวน</label>
              <input
                className="input tnum"
                type="number"
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
            <div>
              <label style={labelStyle}>หน่วย</label>
              <input
                className="input"
                placeholder="เช่น ตัว / ชุด"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>สาขา</label>
              <select
                className="select"
                value={branchId}
                disabled={isEdit}
                onChange={(e) => setBranchId(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              >
                <option value="">— ไม่ระบุ —</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.code} {b.nameTh}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle} id="lva-custodian-label">
                ผู้รับผิดชอบ
              </label>
              <SearchSelect
                value={custodianEmployeeId}
                onChange={setCustodianEmployeeId}
                placeholder="— เลือกพนักงาน —"
                options={employees.map((e) => ({ value: e.id, label: `${e.code} — ${e.nameTh}` }))}
                getLabel={(v) => {
                  const e = employees.find((x) => x.id === v);
                  return e ? `${e.code} — ${e.nameTh}` : '';
                }}
                ariaLabel="ผู้รับผิดชอบ"
              />
            </div>
          </div>
          <div>
            <label style={labelStyle}>สถานที่ใช้งาน</label>
            <input
              className="input"
              placeholder="เช่น สนญ. ชั้น 14 โต๊ะ #14-08"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>
          {!isEdit && (
            <div>
              <label style={labelStyle}>หน่วยงาน</label>
              <DepartmentSelect
                companyId={companyId}
                value={departmentId}
                onChange={setDepartmentId}
                branchId={branchId}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          )}
          {!isEdit && (
            <div
              style={{ padding: '12px 14px', background: '#e8eff8', borderRadius: 6, fontSize: 14 }}
            >
              ระบบจะ<b>ลงบัญชีค่าใช้จ่าย (LVA) อัตโนมัติ / เครดิตบัญชีเงินสด-ธนาคารที่เลือก</b>
              {selectedCashAccount ? (
                <>
                  {' '}
                  (<b className="code">{selectedCashAccount.code}</b> · {selectedCashAccount.nameTh}
                  )
                </>
              ) : null}{' '}
              เต็มจำนวน · ทะเบียน LVA ใช้สำหรับ track เท่านั้น
            </div>
          )}
          {error && (
            <div
              style={{
                padding: '10px 14px',
                background: '#fce9e7',
                borderRadius: 6,
                fontSize: 14,
                color: 'var(--negative)',
              }}
            >
              {error}
            </div>
          )}
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose} disabled={busy}>
            ยกเลิก
          </button>
          <button className="btn primary" onClick={onSubmit} disabled={busy}>
            <Icon name="check" size={14} />{' '}
            {busy ? 'กำลังบันทึก…' : isEdit ? 'บันทึก' : 'บันทึก & ลงค่าใช้จ่าย'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PrintQRModal({
  count,
  companyId,
  onClose,
}: {
  count: number;
  companyId: string | undefined;
  onClose: () => void;
}) {
  // Defaults mirror the prototype's first-visible option in each select:
  // selection = "ทั้งหมดที่ยังไม่พิมพ์" (unprinted), size = "A4 · 8 ดวง".
  const [selection, setSelection] = useState<'all' | 'unprinted' | 'thisMonth'>('unprinted');
  const [size, setSize] = useState<'8' | '24' | '65'>('8');
  // Optional built-in label-printer preset (Phase E). '' = no preset → the user
  // picks the size manually. Selecting a preset drives the size to its layout
  // and surfaces its printer/material metadata; the chosen presetId rides along
  // so the server can apply its margin/gutter overrides.
  const [presetId, setPresetId] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Mutation wrapper → a successful batch print invalidates lva-items so the
  // printed badges refresh (raw downloadLvaLabels bypasses the cache).
  const downloadMut = useDownloadLvaLabels(companyId);

  const selectedPreset = LVA_LABEL_PRESETS.find((p) => p.id === presetId);

  const onPresetChange = (id: string) => {
    setPresetId(id);
    const preset = LVA_LABEL_PRESETS.find((p) => p.id === id);
    if (preset) setSize(preset.layout);
  };

  const handlePrint = async () => {
    if (!companyId || busy) return;
    setError(null);
    setBusy(true);
    try {
      await downloadMut.mutateAsync({
        filter: selection,
        layout: size,
        markPrinted: true,
        ...(presetId ? { presetId } : {}),
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'พิมพ์ไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 580,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3
            style={{ margin: 0, fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-display)' }}
          >
            พิมพ์ QR Tag — Batch Print
          </h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              เลือก LVA
            </label>
            <select
              className="select"
              style={{ width: '100%', height: 38, fontSize: 14 }}
              value={selection}
              onChange={(e) => setSelection(e.target.value as 'all' | 'unprinted' | 'thisMonth')}
            >
              <option value="unprinted">ทั้งหมดที่ยังไม่พิมพ์ ({count})</option>
              <option value="thisMonth">เฉพาะที่เพิ่มเดือนนี้</option>
              <option value="all">ทั้งหมด</option>
            </select>
          </div>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              ขนาด Sticker
            </label>
            <select
              className="select"
              style={{ width: '100%', height: 38, fontSize: 14 }}
              value={size}
              onChange={(e) => setSize(e.target.value as '8' | '24' | '65')}
            >
              <option value="8">A4 · 8 ดวง/หน้า (50×80mm)</option>
              <option value="24">A4 · 24 ดวง/หน้า (35×35mm)</option>
              <option value="65">A4 · 65 ดวง/หน้า (20×20mm)</option>
            </select>
          </div>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              เครื่องพิมพ์ / วัสดุ (Preset)
            </label>
            <select
              className="select"
              style={{ width: '100%', height: 38, fontSize: 14 }}
              value={presetId}
              onChange={(e) => onPresetChange(e.target.value)}
            >
              <option value="">เลือกขนาดเอง (ไม่ใช้ Preset)</option>
              {LVA_LABEL_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            {selectedPreset &&
              (selectedPreset.printerModel != null || selectedPreset.materialType != null) && (
                <div
                  style={{
                    marginTop: 8,
                    padding: '8px 12px',
                    background: '#f5f7fb',
                    borderRadius: 6,
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  {selectedPreset.printerModel && (
                    <span>เครื่องพิมพ์: {selectedPreset.printerModel}</span>
                  )}
                  {selectedPreset.materialType && <span>วัสดุ: {selectedPreset.materialType}</span>}
                </div>
              )}
          </div>
          {error && (
            <div
              style={{
                padding: '10px 14px',
                background: '#fce9e7',
                borderRadius: 6,
                fontSize: 14,
                color: 'var(--negative)',
              }}
            >
              {error}
            </div>
          )}
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          <button className="btn primary" onClick={() => void handlePrint()} disabled={busy}>
            <Icon name="print" size={14} /> {busy ? 'กำลังพิมพ์…' : 'พิมพ์ (PDF)'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CountModal({
  companyId,
  onClose,
}: {
  companyId: string | undefined;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const branchesQuery = useBranches(companyId);
  const departmentsQuery = useDepartments(companyId);
  const categoriesQuery = useLvaCategories(companyId);
  const createMut = useCreateCountRound(companyId);

  const branches = branchesQuery.data ?? [];
  const departments = departmentsQuery.data ?? [];
  const categories = useMemo(() => categoriesQuery.data ?? [], [categoriesQuery.data]);

  const today = new Date().toISOString().slice(0, 10);
  const [period, setPeriod] = useState<string>('');
  const [plannedDate, setPlannedDate] = useState<string>(today);
  const [dueDate, setDueDate] = useState<string>('');
  const [scopeBranchId, setScopeBranchId] = useState<string>('');
  const [scopeDepartmentId, setScopeDepartmentId] = useState<string>('');
  const [scopeCategoryId, setScopeCategoryId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const labelStyle: CSSProperties = {
    fontSize: 14,
    color: 'var(--text-muted)',
    fontWeight: 500,
    display: 'block',
    marginBottom: 6,
  };

  const onSubmit = () => {
    setError(null);
    if (!period.trim()) return setError('กรุณาระบุรอบการตรวจ');
    if (!plannedDate) return setError('กรุณาเลือกวันเริ่มนับ');

    // Scope FKs are all optional (omit = no filter → all in-scope IN_USE items).
    const payload: CreateLvaCountRound = {
      period: period.trim(),
      plannedDate,
      ...(dueDate ? { dueDate } : {}),
      ...(scopeBranchId ? { scopeBranchId } : {}),
      ...(scopeDepartmentId ? { scopeDepartmentId } : {}),
      ...(scopeCategoryId ? { scopeCategoryId } : {}),
    };
    createMut.mutate(payload, {
      onSuccess: (round) => {
        onClose();
        void navigate({ to: '/lva/count/$roundId', params: { roundId: round.id } });
      },
      onError: (e: unknown) => setError(e instanceof Error ? e.message : 'สร้างรอบตรวจนับล้มเหลว'),
    });
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 640,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3
            style={{ margin: 0, fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-display)' }}
          >
            เริ่มรอบตรวจนับ
          </h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>รอบการตรวจ</label>
            <input
              className="input"
              placeholder="เช่น รอบ Q2 2026 (เม.ย.-มิ.ย.)"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
            <div className="meta" style={{ marginTop: 4 }}>
              รหัสรอบ (PCR-YYYY-NNNNN) สร้างอัตโนมัติเมื่อเปิดรอบ
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>วันเริ่มนับ</label>
              <input
                className="input"
                type="date"
                value={plannedDate}
                onChange={(e) => setPlannedDate(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={labelStyle}>กำหนดเสร็จ</label>
              <input
                className="input"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>สาขา (ทั้งหมด)</label>
              <SearchSelect
                value={scopeBranchId}
                onChange={setScopeBranchId}
                placeholder="— ทุกสาขา —"
                options={branches.map((b) => ({ value: b.id, label: `${b.code} ${b.nameTh}` }))}
                getLabel={(v) => {
                  const b = branches.find((x) => x.id === v);
                  return b ? `${b.code} ${b.nameTh}` : '';
                }}
                ariaLabel="สาขา"
              />
            </div>
            <div>
              <label style={labelStyle}>แผนก (ทั้งหมด)</label>
              <SearchSelect
                value={scopeDepartmentId}
                onChange={setScopeDepartmentId}
                placeholder="— ทุกแผนก —"
                options={departments.map((d) => ({ value: d.id, label: `${d.code} ${d.nameTh}` }))}
                getLabel={(v) => {
                  const d = departments.find((x) => x.id === v);
                  return d ? `${d.code} ${d.nameTh}` : '';
                }}
                ariaLabel="แผนก"
              />
            </div>
            <div>
              <label style={labelStyle}>หมวด (ทั้งหมด)</label>
              <SearchSelect
                value={scopeCategoryId}
                onChange={setScopeCategoryId}
                placeholder="— ทุกหมวด —"
                options={categories.map((c) => ({ value: c.id, label: c.nameTh }))}
                getLabel={(v) => categories.find((c) => c.id === v)?.nameTh ?? ''}
                ariaLabel="หมวด"
              />
            </div>
          </div>
          {error && (
            <div
              style={{
                padding: '8px 12px',
                background: 'var(--negative-tint, #fef2f2)',
                color: 'var(--negative)',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button className="btn primary" onClick={onSubmit} disabled={createMut.isPending}>
            <Icon name="check" size={14} /> เริ่มรอบตรวจนับ
          </button>
        </div>
      </div>
    </div>
  );
}

export function LvaDetailModal({
  item,
  onClose,
  onEdit,
}: {
  item: LvaItemListRow;
  onClose: () => void;
  onEdit: (item: LvaItemListRow) => void;
}) {
  // The body (header + facts + movement log + attachments + quick actions +
  // lifecycle sub-modals) lives in the shared LvaDetailView so the modal and the
  // mobile scan-to-detail page render one body and never diverge. This wrapper
  // is just the dimmed overlay + scroll card.
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 680,
          maxWidth: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
        }}
      >
        <LvaDetailView item={item} onClose={onClose} onEdit={onEdit} />
      </div>
    </div>
  );
}
